import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { FiMapPin, FiSave, FiRotateCcw, FiRefreshCw, FiGrid } from 'react-icons/fi';
import * as slotSettingActions from '../../Redux/Actions/slotSettings';
import { toastMessage } from '../../utils/toast';
import WeeklyScheduleEditor from './weeklyScheduleEditor';
import ConfirmModal from './confirmModal';
import { SectionCard, PageLoader, ErrorState, EmptyState, AlertBox, preventEnterSubmit } from './common';
import { CenterSelect, NumberField, SwitchField } from './fields';
import {
    DAY_STATUS, SETTING_FIELDS, VISIBLE_SETTING_FIELDS, scheduleToRows, rowsToSchedule, validateSchedule, validateSettings,
    toNumberOrNull, toInputValue, toCenterId, getErrorMessage, fieldFromMessage,
    formatValue, formatDateTime, hasErrors
} from './helpers';

const toForm = (override) => {
    const form = {};
    SETTING_FIELDS.forEach(({ key }) => {
        const value = override ? override[key] : null;
        form[key] = { useGeneral: value === null || value === undefined, value: toInputValue(value) };
    });
    const sameDay = override ? override.allowSameDayBooking : null;
    form.allowSameDayBooking = { useGeneral: sameDay === null || sameDay === undefined, value: !!sameDay };
    form.rows = scheduleToRows(override?.weeklySchedule, DAY_STATUS.INHERIT);
    return form;
};

const CenterSettingsTab = ({ centers, centersLoading, centersError, onRetryCenters, general, refreshKey, onSaved, onOpenCounters }) => {
    const [centerId, setCenterId] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [counters, setCounters] = useState([]);
    const [form, setForm] = useState(toForm(null));
    const [errors, setErrors] = useState({});
    const [dayErrors, setDayErrors] = useState({});
    const [serverError, setServerError] = useState('');
    const [saving, setSaving] = useState(false);
    const [resetState, setResetState] = useState({ show: false, loading: false, error: '' });
    const [pendingCenter, setPendingCenter] = useState(null);
    const requestId = useRef(0);

    const initialForm = useMemo(() => toForm(data?.override), [data]);
    const isDirty = !!data && JSON.stringify(form) !== JSON.stringify(initialForm);

    const clearErrors = useCallback(() => {
        setErrors({});
        setDayErrors({});
        setServerError('');
    }, []);

    const applyResponse = useCallback((res) => {
        setData(res);
        setForm(toForm(res?.override));
        clearErrors();
    }, [clearErrors]);

    const loadCenter = useCallback((id) => {
        const current = ++requestId.current;
        if (id === '') {
            setData(null);
            setLoadError('');
            setForm(toForm(null));
            return;
        }
        setLoading(true);
        setLoadError('');
        slotSettingActions.getCounters(toCenterId(id)).then(list => {
            if (current === requestId.current) setCounters(Array.isArray(list) ? list : []);
        }).catch(() => {
            if (current === requestId.current) setCounters([]);
        });
        slotSettingActions.getByCenter(toCenterId(id)).then(res => {
            if (current !== requestId.current) return;
            applyResponse(res);
            setLoading(false);
        }).catch(err => {
            if (current !== requestId.current) return;
            setData(null);
            setLoading(false);
            setLoadError(getErrorMessage(err, 'Unable to load center settings'));
        });
    }, [applyResponse]);

    useEffect(() => {
        loadCenter(centerId);
    }, [centerId, loadCenter]);

    useEffect(() => {
        if (refreshKey && centerId !== '' && !isDirty && !saving) loadCenter(centerId);
    }, [refreshKey]);

    const handleCenterChange = (value) => {
        if (isDirty) {
            setPendingCenter(value);
            return;
        }
        setCenterId(value);
    };

    const setFieldState = (key, changes) => {
        setForm(prev => ({ ...prev, [key]: { ...prev[key], ...changes } }));
        if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
    };

    const toggleUseGeneral = (key, useGeneral) => {
        const current = form[key];
        let value = current.value;
        if (!useGeneral && key === 'allowSameDayBooking') value = !!general?.allowSameDayBooking;
        else if (!useGeneral && value === '') value = toInputValue(general?.[key]);
        setFieldState(key, { useGeneral, value });
    };

    const setRows = (rows) => {
        setForm(prev => ({ ...prev, rows }));
        if (hasErrors(dayErrors)) setDayErrors(validateSchedule(rows));
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (saving) return;
        const body = {};
        const fieldErrors = {};
        SETTING_FIELDS.forEach(({ key, label }) => {
            const field = form[key];
            body[key] = field.useGeneral ? null : toNumberOrNull(field.value);
            if (!field.useGeneral && field.value === '') fieldErrors[key] = `Enter ${label.toLowerCase()} or switch on Use General`;
        });
        body.allowSameDayBooking = form.allowSameDayBooking.useGeneral ? null : form.allowSameDayBooking.value;
        body.weeklySchedule = rowsToSchedule(form.rows, true);

        const allErrors = { ...validateSettings(body), ...fieldErrors };
        const scheduleErrors = validateSchedule(form.rows);
        setErrors(allErrors);
        setDayErrors(scheduleErrors);
        setServerError('');
        if (hasErrors(allErrors) || hasErrors(scheduleErrors)) {
            toastMessage('error', 'Please fix the highlighted fields');
            return;
        }

        setSaving(true);
        slotSettingActions.updateByCenter(toCenterId(centerId), body).then(res => {
            setSaving(false);
            applyResponse(res);
            toastMessage('success', `Settings saved for ${res?.centerName || 'center'}`);
            onSaved(res);
        }).catch(err => {
            setSaving(false);
            const message = getErrorMessage(err, 'Unable to save center settings');
            const field = fieldFromMessage(message);
            if (field && SETTING_FIELDS.some(f => f.key === field)) setErrors({ [field]: message });
            setServerError(message);
            toastMessage('error', message);
        });
    };

    const handleReset = () => {
        setResetState({ show: true, loading: true, error: '' });
        slotSettingActions.resetCenter(toCenterId(centerId)).then(res => {
            setResetState({ show: false, loading: false, error: '' });
            applyResponse(res);
            toastMessage('success', `${res?.centerName || 'Center'} now follows General settings`);
            onSaved(res);
        }).catch(err => {
            setResetState({ show: true, loading: false, error: getErrorMessage(err, 'Unable to reset center settings') });
        });
    };

    const effective = data?.effective;
    const hasOverride = !!data?.override;

    const renderBody = () => {
        if (centersLoading) return <SectionCard><PageLoader text="Loading centers..." /></SectionCard>;
        if (centersError) return <SectionCard><ErrorState title="Could not load centers" message={centersError} onRetry={onRetryCenters} /></SectionCard>;
        if (centerId === '') {
            return (
                <SectionCard>
                    <EmptyState icon={<FiMapPin />} title="Select a center"
                        text="Pick a center above to see its settings. Fields you don't change keep following the General settings." />
                </SectionCard>
            );
        }
        if (loading) return <SectionCard><PageLoader text="Loading center settings..." /></SectionCard>;
        if (loadError) return <SectionCard><ErrorState message={loadError} onRetry={() => loadCenter(centerId)} /></SectionCard>;
        if (!data) return null;

        return (
            <Form onSubmit={handleSave} onKeyDown={preventEnterSubmit} noValidate>
                {serverError && <AlertBox title="Settings were not saved" messages={serverError} onClose={() => setServerError('')} />}
                {counters.length > 0 && (
                    <div className="ss-counter-notice">
                        <FiGrid className="ss-counter-notice-icon" />
                        <div>
                            <strong>This center uses {counters.length} counter{counters.length > 1 ? 's' : ''} ({counters.filter(c => c.isActive).length} active).</strong>
                            <span>Seats at each time = the active counters open then (1 counter = 1 seat), so <b>Seats per slot</b> below is not used here. The weekly schedule, holidays and slot duration still apply.</span>
                        </div>
                        {onOpenCounters && (
                            <Button variant="outline-primary" size="sm" onClick={() => onOpenCounters(centerId)}>Open Counters</Button>
                        )}
                    </div>
                )}
                <Row className="gx-4">
                    <Col xl={8}>
                        <SectionCard
                            title="Booking rules"
                            subtitle="Switch off Use General to give this center its own value."
                            bodyClassName="p-0"
                        >
                            {VISIBLE_SETTING_FIELDS.map(field => {
                                const state = form[field.key];
                                return (
                                    <div key={field.key} className={`ss-field-row ${errors[field.key] ? 'has-error' : ''}`}>
                                        <div>
                                            <div className="ss-field-name">{field.label}</div>
                                            <div className="ss-field-general">General: {formatValue(field.key, general?.[field.key])}</div>
                                        </div>
                                        <SwitchField id={`c-use-${field.key}`} label="Use General"
                                            checked={state.useGeneral} disabled={saving}
                                            onChange={checked => toggleUseGeneral(field.key, checked)} />
                                        <div>
                                            <NumberField
                                                id={`c-${field.key}`}
                                                value={state.useGeneral ? '' : state.value}
                                                placeholder={state.useGeneral ? 'Inherited' : ''}
                                                suffix={field.suffix}
                                                min={field.min}
                                                max={field.max}
                                                error={errors[field.key]}
                                                disabled={state.useGeneral || saving}
                                                onChange={value => setFieldState(field.key, { value })}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                            <div className="ss-field-row">
                                <div>
                                    <div className="ss-field-name">Same-day booking</div>
                                    <div className="ss-field-general">General: {general ? (general.allowSameDayBooking ? 'Allowed' : 'Not allowed') : '-'}</div>
                                </div>
                                <SwitchField id="c-use-allowSameDayBooking" label="Use General"
                                    checked={form.allowSameDayBooking.useGeneral} disabled={saving}
                                    onChange={checked => toggleUseGeneral('allowSameDayBooking', checked)} />
                                <div className={`ss-toggle-tile ${form.allowSameDayBooking.useGeneral ? 'is-disabled' : ''}`}>
                                    <span>
                                        {form.allowSameDayBooking.useGeneral ? 'Inherited' : form.allowSameDayBooking.value ? 'Allowed' : 'Not allowed'}
                                    </span>
                                    <SwitchField id="c-allowSameDayBooking"
                                        checked={!form.allowSameDayBooking.useGeneral && form.allowSameDayBooking.value}
                                        disabled={form.allowSameDayBooking.useGeneral || saving}
                                        onChange={checked => setFieldState('allowSameDayBooking', { value: checked })} />
                                </div>
                            </div>
                        </SectionCard>
                    </Col>

                    <Col xl={4}>
                        <div className="ss-effective h-100">
                            <SectionCard title="Effective values" subtitle="What the slot APIs use for this center right now.">
                                {effective ? (
                                    <>
                                        {VISIBLE_SETTING_FIELDS.map(field => (
                                            <div className="ss-kv" key={field.key}>
                                                <span className="ss-kv-label">{field.label}</span>
                                                <span className="ss-kv-value">{formatValue(field.key, effective[field.key])}</span>
                                            </div>
                                        ))}
                                        <div className="ss-kv">
                                            <span className="ss-kv-label">Same-day booking</span>
                                            <span className="ss-kv-value">{effective.allowSameDayBooking ? 'Allowed' : 'Not allowed'}</span>
                                        </div>
                                    </>
                                ) : <span className="text-muted">No data</span>}
                            </SectionCard>
                        </div>
                    </Col>
                </Row>

                <SectionCard
                    title="Weekly schedule"
                    subtitle="Days marked General follow the General schedule. Click a day and switch off Same as General to give it this center's own rule."
                    bodyClassName="p-0"
                    footer={(
                        <div className="ss-actions">
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                {isDirty && <span className="ss-dirty">Unsaved changes</span>}
                                {data.override?.updatedTime && <span className="ss-meta">Last updated {formatDateTime(data.override.updatedTime)}</span>}
                            </div>
                            <div className="ss-actions-buttons flex-wrap">
                                <Button variant="outline-danger" disabled={saving || !hasOverride}
                                    title={hasOverride ? '' : 'This center already follows General'}
                                    onClick={() => setResetState({ show: true, loading: false, error: '' })}>
                                    <FiRefreshCw /> Reset to General
                                </Button>
                                <Button variant="light" disabled={!isDirty || saving} onClick={() => { setForm(initialForm); clearErrors(); }}>
                                    <FiRotateCcw /> Discard
                                </Button>
                                <Button variant="primary" type="submit" disabled={!isDirty || saving}>
                                    <FiSave /> {saving ? 'Saving...' : 'Save changes'}
                                </Button>
                            </div>
                        </div>
                    )}
                >
                    <WeeklyScheduleEditor rows={form.rows} onChange={setRows} isCenter
                        generalSchedule={general?.weeklySchedule} errors={dayErrors} disabled={saving} />
                </SectionCard>
            </Form>
        );
    };

    return (
        <>
            <SectionCard>
                <Row className="g-3 align-items-end">
                    <Col md={6} lg={4}>
                        <Form.Label htmlFor="c-center">Center</Form.Label>
                        <CenterSelect id="c-center" value={centerId} centers={centers} loading={centersLoading}
                            disabled={saving || centersLoading || !!centersError}
                            onChange={handleCenterChange} />
                    </Col>
                    {data && !loading && (
                        <Col md={6} lg={8}>
                            {hasOverride
                                ? <span className="ss-pill is-custom">Has its own settings</span>
                                : <span className="ss-pill is-primary">Follows General settings</span>}
                        </Col>
                    )}
                </Row>
            </SectionCard>

            {renderBody()}

            <ConfirmModal
                show={resetState.show}
                title="Reset to General"
                message={<>Remove all own settings for <strong>{data?.centerName || 'this center'}</strong>? The center will follow the General settings again. Holidays and special days are not affected.</>}
                confirmLabel="Reset"
                loading={resetState.loading}
                error={resetState.error}
                onConfirm={handleReset}
                onClose={() => setResetState({ show: false, loading: false, error: '' })}
            />

            <ConfirmModal
                show={pendingCenter !== null}
                title="Discard unsaved changes?"
                message="You have unsaved changes for this center. Switching center will discard them."
                confirmLabel="Discard and switch"
                variant="primary"
                onConfirm={() => { setCenterId(pendingCenter); setPendingCenter(null); }}
                onClose={() => setPendingCenter(null)}
            />
        </>
    );
};

export default CenterSettingsTab;
